import { uploadPendingPhotos } from './photoQueue';
import { rejectsPayment } from './payment';
import type { PosStore } from './store';
import type {
  CatalogItem,
  SnapshotCapabilities,
  SyncApi,
  SyncResponse,
  VoidEntry,
  VoidPayload,
} from './types';

/** So lange wartet die Kasse, bevor sie den Snapshot vollständig neu lädt, solange der Server noch keine Artikelnummern liefert. */
export const ITEM_NO_RETRY_MS = 30 * 60 * 1000;

/** Kleine Pakete: Auch bei schlechtem Netz kommen sie an (SYN-04). */
export const CHUNK_SIZE = 20;

/** Kommentare je Aufruf (der Server nimmt höchstens 20 an). */
const NOTE_CHUNK = 20;

export interface SyncSummary {
  /** In der Antwort bestätigte Verkäufe (angenommen oder schon bekannt). */
  confirmed: number;
  /** Vom Server abgelehnte Verkäufe: bleiben sichtbar, werden nicht wiederholt. */
  rejected: number;
  /** Bestätigte Stornos. */
  voided: number;
  /** Vom Server gemeldete Konfliktzeilen (z. B. Artikel auf zwei Kassen verkauft). */
  conflicts: number;
  /** Aus dem Snapshot übernommene Artikel. */
  pulled: number;
  /**
   * Die Artikelliste war nicht mehr abrufbar (401 nach erfolgreichem Senden: Basar geschlossen oder „Nur noch
   * hochladen“, E11). Das Senden ging durch, das Gerät ist also nicht abgekoppelt.
   */
  snapshotUnavailable?: boolean;
}

/** Antwort 400 (Prüfung der Anfrage): Der Server kennt ein gesendetes Feld nicht. */
const isBadRequest = (error: unknown): boolean =>
  typeof error === 'object' && error !== null && 'status' in error && error.status === 400;

/** Fehler der Gerätesitzung (`DEVICE_REVOKED`): am Snapshot nach erfolgreichem Senden heißt das „nur noch hochladen“. */
const isDeviceRevoked = (error: unknown): boolean =>
  typeof error === 'object' && error !== null && 'code' in error && error.code === 'DEVICE_REVOKED';

/**
 * Storno im Stapel. Grund und Text gehen NUR mit, wenn der zuletzt geladene Stand den Grund in `capabilities.voidReasons`
 * führt (U17d): Ein älteres Image weist unbekannte Felder mit 400 ab, und zwar den ganzen Stapel. Der Grund bleibt sonst auf
 * dem Gerät (Verlauf, Notausgang).
 */
export function voidPayload(entry: VoidEntry, capabilities: SnapshotCapabilities): VoidPayload {
  const sendReason =
    entry.reason !== undefined && capabilities.voidReasons?.includes(entry.reason) === true;
  return {
    saleClientUuid: entry.saleClientUuid,
    afterSeq: entry.afterSeq,
    ...(entry.lineNo === undefined ? {} : { lineNo: entry.lineNo }),
    ...(sendReason && entry.reason !== undefined ? { reason: entry.reason } : {}),
    ...(sendReason && entry.note !== undefined ? { note: entry.note } : {}),
  };
}

/**
 * Sync der Kasse (SYN-01 bis SYN-05): erst senden (Outbox in kleinen Paketen), dann Änderungen holen (Cursor mit
 * Löschvermerken). Ein Verkauf verlässt die Outbox erst nach der Bestätigung des Servers. Jeder Schritt ist idempotent:
 * bricht das Netz mitten in einer Anfrage ab, wird beim nächsten Lauf dasselbe noch einmal gesendet und vom Server als
 * Duplikat erkannt.
 */
export class SyncEngine {
  /** Zähler der vom Server angenommenen Sync-Aufrufe (Vergleich vor und nach einem Lauf). */
  private acceptedCalls = 0;

  constructor(
    private readonly store: PosStore,
    private readonly api: SyncApi,
    private readonly chunkSize: number = CHUNK_SIZE,
    /**
     * Rückruf je Seite des Abzugs mit den Artikeln, wie sie gespeichert wurden (Suchindex der Kasse, U17b). Er sieht damit
     * dieselben Status wie der Speicher: Ein lokal verkaufter, noch nicht gesendeter Artikel bleibt auch dort „verkauft“.
     */
    private readonly onItems?: (items: readonly CatalogItem[]) => void,
  ) {}

  async runOnce(): Promise<SyncSummary> {
    const summary: SyncSummary = { confirmed: 0, rejected: 0, voided: 0, conflicts: 0, pulled: 0 };
    const acceptedBefore = this.acceptedCalls;
    await this.push(summary);
    try {
      await this.pull(summary);
    } catch (error) {
      // Nach „Geschlossen“ oder „Nur noch hochladen“ liefert nur der Snapshot 401, das Senden geht aber durch: kein
      // „abgekoppelt“, sondern „Artikelliste nicht mehr verfügbar“. Abgekoppelt ist das Gerät erst, wenn auch der Sync
      // abgelehnt wird. War in diesem Lauf nichts zu senden (leere Warteschlange), prüft eine leere Sync-Anfrage das.
      if (!isDeviceRevoked(error)) throw error;
      if (this.acceptedCalls === acceptedBefore) await this.probeSync();
      summary.snapshotUnavailable = true;
    }
    return summary;
  }

  /** Leere Sync-Anfrage: Wirft (z. B. `DEVICE_REVOKED`), wenn der Server das Senden dieser Kasse nicht mehr annimmt. */
  private async probeSync(): Promise<void> {
    const register = await this.store.register();
    if (register === undefined) throw new Error('Die Kasse ist noch nicht eröffnet.');
    await this.api.sync(register.registerId, {
      sales: [],
      voids: [],
      client: await this.store.counters(),
    });
    this.acceptedCalls += 1;
  }

  async push(
    summary: SyncSummary = { confirmed: 0, rejected: 0, voided: 0, conflicts: 0, pulled: 0 },
  ): Promise<SyncSummary> {
    const register = await this.store.register();
    if (register === undefined) throw new Error('Die Kasse ist noch nicht eröffnet.');
    for (;;) {
      const batch = await this.store.batch(this.chunkSize);
      if (batch.sales.length === 0 && batch.voids.length === 0) break;
      let response: SyncResponse;
      const capabilities = await this.store.capabilities();
      const voids = batch.voids.map((entry) => voidPayload(entry, capabilities));
      try {
        response = await this.api.sync(register.registerId, {
          sales: batch.sales.map((entry) => entry.payload),
          voids,
          client: await this.store.counters(),
        });
      } catch (error) {
        // Ein Verkauf mit `promoId` an einen Server, der Aktionen nicht (mehr) kennt, wird mit 400 abgewiesen (z. B. nach einem
        // Rollback des Images). Nichts wird gelöscht: Die Kasse hält Verkäufe mit `promoId` zurück, bis ein Snapshot wieder
        // `promos` zeigt, und sendet den Rest weiter.
        const withPromo = batch.sales.some(
          (entry) =>
            Array.isArray(entry.payload.lines) &&
            entry.payload.lines.some((line) => line.promoId !== undefined),
        );
        // Stornos mit Grund (U17d): Anders als bei der Aktion wird nichts zurückgehalten, denn das Storno ist wichtiger als sein
        // Grund. Die Fähigkeiten werden zurückgesetzt und der Stapel SOFORT ohne Grund erneut gesendet; der Grund bleibt auf dem
        // Gerät.
        const withReason = voids.some((entry) => entry.reason !== undefined);
        // Preiskorrektur (U17g): Das Feld wird aus den noch nie angenommenen Verkäufen entfernt und der Stapel sofort erneut
        // gesendet. Der Preis der Zeile ist schon der korrigierte, es wird nichts abgelehnt und nichts geht verloren.
        const withPriceChange = batch.sales.some(
          (entry) =>
            Array.isArray(entry.payload.lines) &&
            entry.payload.lines.some((line) => line.priceChange !== undefined),
        );
        // Artikel ohne Etikett (U17h): ebenso, die Zeile geht dann als unbekannter Code mit Preis hinaus
        const withManual = batch.sales.some(
          (entry) =>
            Array.isArray(entry.payload.lines) &&
            entry.payload.lines.some((line) => line.manual !== undefined),
        );
        // Zahlart Karte (U14): ebenso, die Verkäufe gehen als bar hinaus (die Markierung bleibt im Verlauf der Kasse)
        const withCard =
          batch.sales.some((entry) => entry.payload.payment === 'card') &&
          isBadRequest(error) &&
          rejectsPayment(error);
        if (
          !(withPromo || withReason || withPriceChange || withManual || withCard) ||
          !isBadRequest(error)
        ) {
          throw error;
        }
        if (withPromo) await this.store.setPromos(undefined);
        if (withReason || withPriceChange || withManual || withCard)
          await this.store.setCapabilities(undefined);
        if (withCard) await this.store.stripCardPayments();
        if (withPriceChange) await this.store.stripPriceChanges();
        if (withManual) await this.store.stripManualLines();
        continue;
      }
      this.acceptedCalls += 1;
      await this.store.applySyncResult(response);
      let progress = 0;
      for (const outcome of response.sales) {
        if (outcome.status === 'rejected') summary.rejected += 1;
        else summary.confirmed += 1;
        summary.conflicts += outcome.lines.filter((line) => line.result === 'conflict').length;
        progress += 1;
      }
      for (const outcome of response.voids) {
        if (outcome.status === 'voided' || outcome.status === 'already_voided') {
          summary.voided += 1;
          progress += 1;
        }
      }
      // Nichts wurde erledigt (z. B. ein Storno, dessen Verkauf der Server noch nicht kennt): später wieder versuchen
      if (progress === 0) break;
    }
    await this.pushNotes();
    await this.pushPhotos();
    return summary;
  }

  /**
   * Kommentare der Kasse (U17f) gehen NACH den Verkäufen und Stornos hinaus, nur wenn der Stand `capabilities.registerNotes`
   * meldete. Bei 400 (älteres Image) wird die Fähigkeit zurückgesetzt, die Kommentare bleiben im Speicher und gehen später.
   * Ein Fehler hier wirft nie: Verkäufe sind schon bestätigt, und ein Kommentar ist kein Geld.
   */
  private async pushPhotos(): Promise<void> {
    if ((await this.store.capabilities()).manualLines !== true) return;
    const register = await this.store.register();
    if (register === undefined) return;
    try {
      await uploadPendingPhotos(this.store, this.api, register.registerId);
    } catch {
      // Ein Foto ist kein Geld: Der nächste Lauf versucht es wieder
    }
  }

  private async pushNotes(): Promise<void> {
    if ((await this.store.capabilities()).registerNotes !== true) return;
    const register = await this.store.register();
    if (register === undefined) return;
    for (;;) {
      const batch = await this.store.noteBatch(NOTE_CHUNK);
      if (batch.length === 0) return;
      let response: SyncResponse;
      try {
        response = await this.api.sync(register.registerId, {
          sales: [],
          voids: [],
          notes: batch.map((entry) => ({
            clientUuid: entry.clientUuid,
            ...(entry.saleClientUuid === undefined ? {} : { saleClientUuid: entry.saleClientUuid }),
            ...(entry.lineNo === undefined ? {} : { lineNo: entry.lineNo }),
            topics: entry.topics,
            ...(entry.text === undefined ? {} : { text: entry.text }),
            createdAt: entry.createdAt,
          })),
          client: await this.store.counters(),
        });
      } catch (error) {
        // Netz- und Sitzungsfehler: der nächste Lauf versucht es wieder
        if (isBadRequest(error)) await this.store.setCapabilities(undefined);
        return;
      }
      this.acceptedCalls += 1;
      const outcomes = response.notes ?? [];
      await this.store.applyNoteResults(outcomes);
      // Kein Fortschritt (z. B. alles `unknown_sale`): später wieder versuchen
      if (
        !outcomes.some((outcome) => outcome.status === 'saved' || outcome.status === 'duplicate')
      ) {
        return;
      }
    }
  }

  async pull(
    summary: SyncSummary = { confirmed: 0, rejected: 0, voided: 0, conflicts: 0, pulled: 0 },
  ): Promise<SyncSummary> {
    let since = await this.store.getMeta<string>('cursor');
    // Lieferte der Server bisher keine `itemNo` (älterer Stand), ginge ein späteres Update nur für geänderte Artikel durch den
    // Cursor: In größeren Abständen wird deshalb vollständig neu geladen, bis die Nummern da sind
    const lastGap = await this.store.getMeta<number>('itemNoMissingAt');
    if (lastGap !== undefined && Date.now() - lastGap > ITEM_NO_RETRY_MS) since = undefined;
    let lacking = false;
    let afterId: string | undefined;
    for (;;) {
      const page = await this.api.snapshot({
        ...(since === undefined ? {} : { since }),
        ...(afterId === undefined ? {} : { afterId }),
      });
      if (page.items.some((entry) => !entry.deleted && entry.itemNo === undefined)) lacking = true;
      const stored = await this.store.applySnapshot(page.items);
      this.onItems?.(stored);
      await this.store.updateSalt(page.labelSalt);
      // Aktionen (X7): fehlt das Feld, kennt der Server `promoId` nicht und die Kasse sendet es nicht
      await this.store.setPromos(page.promos);
      // Zusatzfelder (U17): fehlt das Feld, kennt der Server keines davon, und die Kasse sendet keines
      await this.store.setCapabilities(page.capabilities);
      summary.pulled += page.items.length;
      if (!page.more || page.next === null) {
        // Der Cursor wird erst nach der letzten Seite gespeichert: Bricht der Abzug ab, beginnt er beim nächsten Mal neu
        await this.store.setMeta('cursor', page.cursor);
        await this.store.setMeta('snapshotAt', page.serverTime);
        await this.store.setMeta('itemNoMissingAt', lacking ? Date.now() : undefined);
        return summary;
      }
      since = page.next.since;
      afterId = page.next.afterId;
    }
  }
}
